import React, { useMemo, useState } from 'react';
import {
	Badge,
	Box,
	Button,
	Chip,
	Collapse,
	Divider,
	Grid,
	Paper,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import makeStyles from '@mui/styles/makeStyles';
import { FilterModel, FilterType } from 'features/common/types';
import { getFilterModalTitle } from 'features/common/helpers';
import { advancedListFilters, QUICK_FILTER_KEYS } from './constants';

interface PassengersFiltersProps {
	onFiltersChange: (filters: FilterModel[]) => void;
}

const useStyles = makeStyles((theme) => ({
	chipWrap: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(1),
	},
}));

export const PassengersFilters = ({ onFiltersChange }: PassengersFiltersProps): JSX.Element => {
	const classes = useStyles();
	const [filterApplied, setFilterApplied] = useState<FilterModel[]>([]);
	const [expanded, setExpanded] = useState(false);

	const quickFilters = useMemo(
		() => advancedListFilters.filter((filter) => QUICK_FILTER_KEYS.includes(filter.key)),
		[],
	);
	const expandedFilters = useMemo(
		() => advancedListFilters.filter((filter) => !QUICK_FILTER_KEYS.includes(filter.key)),
		[],
	);

	const updateFilters = (updater: (prev: FilterModel[]) => FilterModel[]) => {
		setFilterApplied((prev) => {
			const next = updater(prev);
			onFiltersChange(next);
			return next;
		});
	};

	const handleApplyFilter = (filter: FilterModel) => {
		updateFilters((prev) => {
			const next = [...prev];
			const index = next.findIndex((item) => item.key === filter.key);
			if (index !== -1) {
				next[index] = filter;
			} else {
				next.push(filter);
			}
			return next;
		});
	};

	const handleDeleteFilter = (filter: FilterModel) => {
		updateFilters((prev) => prev.filter((item) => item.key !== filter.key));
	};

	const handleDeleteAllFilters = () => updateFilters(() => []);

	const getAppliedFilter = (filter: FilterModel): FilterModel => {
		return filterApplied.find((item) => item.key === filter.key) ?? filter;
	};

	const handleValueChange = (filter: FilterModel, value: string) => {
		if (!value.trim()) {
			handleDeleteFilter(filter);
			return;
		}
		handleApplyFilter({ ...filter, value: value.trim() });
	};

	const getChipLabel = (filter: FilterModel): string => {
		if (filter.type === FilterType.STRING) {
			return `${getFilterModalTitle(filter)} ${filter.value}`;
		}
		return `${filter.label} ${filter.value}`;
	};

	const renderInlineFilter = (filter: FilterModel) => {
		const applied = getAppliedFilter(filter);
		return (
			<TextField
				fullWidth
				size="small"
				label={filter.label}
				value={applied.value ?? ''}
				onChange={(event) => handleValueChange(filter, event.target.value)}
			/>
		);
	};

	const activeCount = filterApplied.length;

	return (
		<Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2 }}>
			<Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
				<Box display="flex" alignItems="center" gap={1}>
					<Badge badgeContent={activeCount} color="primary">
						<FilterListIcon color="action" />
					</Badge>
					<Typography variant="subtitle1" fontWeight={500} component="h2">
						Filtros
					</Typography>
				</Box>

				<Box display="flex" gap={1}>
					<Tooltip title="Limpiar todos los filtros">
						<span>
							<Button
								size="small"
								startIcon={<ClearIcon />}
								onClick={handleDeleteAllFilters}
								variant="outlined"
								color="error"
								disabled={activeCount === 0}
							>
								Limpiar
							</Button>
						</span>
					</Tooltip>
					{expandedFilters.length > 0 && (
						<Tooltip title={expanded ? 'Ocultar filtros' : 'Mostrar más filtros'}>
							<Button
								size="small"
								endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
								onClick={() => setExpanded((prev) => !prev)}
								variant="outlined"
								color="primary"
							>
								{expanded ? 'Menos filtros' : 'Más filtros'}
							</Button>
						</Tooltip>
					)}
				</Box>
			</Box>

			{activeCount > 0 && (
				<Box className={classes.chipWrap} mb={1.5}>
					{filterApplied.map((filter) => (
						<Chip
							key={`chip-${filter.key}`}
							label={getChipLabel(filter)}
							onDelete={() => handleDeleteFilter(filter)}
							color="primary"
							variant="outlined"
							size="small"
						/>
					))}
				</Box>
			)}

			<Grid container spacing={1.5}>
				{quickFilters.map((filter) => (
					<Grid item key={`quick-${filter.key}`} xs={12} sm={6} md={4}>
						{renderInlineFilter(filter)}
					</Grid>
				))}
			</Grid>

			{expandedFilters.length > 0 && (
				<Collapse in={expanded}>
					<Divider sx={{ my: 1.5 }} />
					<Grid container spacing={1.5}>
						{expandedFilters.map((filter) => (
							<Grid item key={`expanded-${filter.key}`} xs={12} sm={6} md={4}>
								{renderInlineFilter(filter)}
							</Grid>
						))}
					</Grid>
				</Collapse>
			)}
		</Paper>
	);
};
